import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { notificar } from '../utils/notify';

// Cadastro das ATIVIDADES por cliente e tipo de processo (Admissão, Rescisão,
// Férias) — o template que define quais tarefas entram no checklist de cada
// novo processo e o status inicial de cada uma.

const CORES_STATUS = {
  'Concluído': { classe: 'bg-success', icone: 'fa-circle-check' },
  'Não iniciada': { classe: 'bg-warning text-dark', icone: 'fa-clock' },
  'Não se aplica': { classe: 'bg-secondary', icone: 'fa-minus-circle' },
};

// Mesmos valores aceitos pelo backend (_TIPOS_ATIVIDADE em core/views.py)
const TIPOS = ['Admissão', 'Rescisão', 'Férias'];

const ICONE_TIPO = {
  'Admissão': 'fa-user-plus',
  'Rescisão': 'fa-user-minus',
  'Férias': 'fa-umbrella-beach',
};

const BpoRhAtividadesAdmissao = () => {
  const [clientes, setClientes] = useState([]);
  const [clienteId, setClienteId] = useState('');
  // aba inicial pode vir da URL (?tipo=Rescisão) — é como as páginas de
  // Admissões/Rescisões/Férias abrem esta tela já no template certo
  const [searchParams] = useSearchParams();
  const tipoUrl = searchParams.get('tipo');
  const [tipo, setTipo] = useState(TIPOS.includes(tipoUrl) ? tipoUrl : 'Admissão');
  const [totaisPorTipo, setTotaisPorTipo] = useState({});
  const [atividades, setAtividades] = useState([]);
  const [statusDisponiveis, setStatusDisponiveis] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [salvandoId, setSalvandoId] = useState(null);

  useEffect(() => { notificar(mensagem); }, [mensagem]);

  // nova atividade
  const [novaAtividade, setNovaAtividade] = useState('');
  const [novoStatus, setNovoStatus] = useState('Não iniciada');
  const [criando, setCriando] = useState(false);

  // exclusão / replicação
  const [excluir, setExcluir] = useState(null);
  const [replicando, setReplicando] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
  const API = `${process.env.REACT_APP_API_BASE}/api/bporh/atividades-admissao/`;

  const carregar = useCallback(() => {
    setCarregando(true);
    axios
      .get(API, {
        params: clienteId ? { cliente_id: clienteId, tipo } : { tipo },
        headers: headers(),
      })
      .then((res) => {
        setClientes(res.data.clientes || []);
        setAtividades(res.data.atividades || []);
        setStatusDisponiveis(res.data.status_disponiveis || []);
        setTotaisPorTipo(res.data.totais_por_tipo || {});
        setCarregando(false);
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.erro || 'Erro ao carregar as atividades'}`);
        setCarregando(false);
      });
  }, [clienteId, tipo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { carregar(); }, [carregar]);

  // Abre já no primeiro cliente da lista — sem seleção a tela nasce vazia e o
  // template de atividades é sempre por cliente, nunca uma visão consolidada.
  useEffect(() => {
    if (!clienteId && clientes.length > 0) setClienteId(String(clientes[0].id));
  }, [clientes]); // eslint-disable-line react-hooks/exhaustive-deps

  const alterarStatus = (atv, status) => {
    setSalvandoId(atv.id);
    // atualização otimista: a linha reflete a escolha na hora
    setAtividades((lista) => lista.map(a => a.id === atv.id ? { ...a, status_inicial: status } : a));
    axios
      .patch(`${API}${atv.id}/`, { status_inicial: status }, { headers: headers() })
      .then(() => setMensagem(`${atv.atividade}: ${status}`))
      .catch(() => { setMensagem('Não foi possível salvar'); carregar(); })
      .finally(() => setSalvandoId(null));
  };

  const criarAtividade = () => {
    if (!clienteId) { setMensagem('Selecione o cliente'); return; }
    if (!novaAtividade.trim()) { setMensagem('Informe o nome da atividade'); return; }
    setCriando(true);
    axios
      .post(API, {
        bporh_cliente_id: clienteId, tipo,
        atividade: novaAtividade, status_inicial: novoStatus,
      }, { headers: headers() })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        setNovaAtividade(''); setNovoStatus('Não iniciada');
        carregar();
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Erro ao cadastrar'}`))
      .finally(() => setCriando(false));
  };

  const confirmarExclusao = () => {
    axios
      .delete(`${API}${excluir.id}/`, { headers: headers() })
      .then(() => { setMensagem('Atividade removida'); setExcluir(null); carregar(); })
      .catch(() => setMensagem('Não foi possível remover'));
  };

  const replicarParaTodos = () => {
    if (!clienteId) return;
    setReplicando(true);
    axios
      .post(`${API}replicar/`, { origem_id: clienteId, tipo, destinos: 'todos' }, { headers: headers() })
      .then((res) => setMensagem(`${res.data.mensagem}`))
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Erro ao replicar'}`))
      .finally(() => setReplicando(false));
  };

  const clienteAtual = clientes.find(c => String(c.id) === String(clienteId));
  const resumo = atividades.reduce((acc, a) => {
    acc[a.status_inicial] = (acc[a.status_inicial] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="bporh-atividades-page">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        {/* Cabeçalho */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-list-check header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">BPO RH — Atividades</h2>
          </div>
          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select"
              style={{ width: 'auto', minWidth: '200px' }}
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
            >
              <option value="">Selecione o cliente...</option>
              {clientes.map(c => (
                <option key={c.id} value={c.id}>{c.cliente}{c.operador ? ` · ${c.operador}` : ''}</option>
              ))}
            </select>
            {clienteId && (
              <button className="btn btn-outline-primary" onClick={replicarParaTodos} disabled={replicando}
                title={`Copia as atividades de ${tipo.toLowerCase()} deste cliente para todos os outros`}>
                {replicando
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Replicando...</>
                  : <><i className="fas fa-copy me-2"></i>Replicar para todos</>}
              </button>
            )}
          </div>
        </div>

        {/* Abas por tipo de processo — cada tipo tem seu próprio template */}
        <ul className="nav nav-pills mb-3 gap-2">
          {TIPOS.map(t => (
            <li className="nav-item" key={t}>
              <button
                type="button"
                className={`nav-link ${t === tipo ? 'active' : 'text-dark'}`}
                style={t === tipo ? { backgroundColor: '#00205b' } : { backgroundColor: '#f2f4f8' }}
                onClick={() => setTipo(t)}
              >
                <i className={`fas ${ICONE_TIPO[t]} me-2`}></i>
                {t}
                {clienteId && (
                  <span className={`badge ms-2 ${t === tipo ? 'bg-light text-dark' : 'bg-secondary'}`}>
                    {totaisPorTipo[t] || 0}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>

        <div className="alert alert-info border-start border-info border-3 small">
          <i className="fas fa-info-circle me-2"></i>
          Este é o <strong>template do checklist de {tipo.toLowerCase()}</strong>: define quais
          atividades entram no processo de cada cliente e como cada uma nasce. Use
          <strong> Não se aplica</strong> para o que o cliente não usa e
          <strong> Não iniciada</strong> para o que a equipe precisa executar.
        </div>

        {!clienteId ? (
          <div className="alert alert-light border">
            <i className="fas fa-hand-point-up me-2"></i>
            Selecione um cliente acima para ver e editar as atividades de {tipo.toLowerCase()}.
          </div>
        ) : carregando ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="text-muted mt-2 mb-0">Carregando atividades...</p>
          </div>
        ) : (
          <>
            {/* Resumo */}
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <span className="badge fs-6" style={{ backgroundColor: '#00205b' }}>
                <i className="fas fa-building me-1"></i>{clienteAtual?.cliente}
              </span>
              <span className="badge" style={{ backgroundColor: '#00b5cc' }}>
                <i className={`fas ${ICONE_TIPO[tipo]} me-1`}></i>{tipo}
              </span>
              <span className="badge bg-secondary">{atividades.length} atividades</span>
              {Object.entries(resumo).map(([status, qtd]) => (
                <span key={status} className={`badge ${CORES_STATUS[status]?.classe || 'bg-light text-dark'}`}>
                  <i className={`fas ${CORES_STATUS[status]?.icone || 'fa-circle'} me-1`}></i>
                  {qtd} {status.toLowerCase()}
                </span>
              ))}
            </div>

            {/* Tabela de atividades */}
            {atividades.length === 0 && (
              <div className="alert alert-light border">
                <i className="fas fa-inbox me-2"></i>
                Este cliente ainda não tem atividades de <strong>{tipo.toLowerCase()}</strong>{' '}
                cadastradas. Adicione abaixo, ou monte o template em um cliente e use
                <strong> Replicar para todos</strong>.
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle">
                <thead>
                  <tr>
                    <th style={{ width: '60px' }}>#</th>
                    <th><i className="fas fa-tasks me-2"></i>Atividade</th>
                    <th style={{ width: '260px' }}><i className="fas fa-flag me-2"></i>Status inicial</th>
                    <th className="text-center" style={{ width: '90px' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {atividades.map((a, i) => (
                    <tr key={a.id}>
                      <td className="text-muted">{i + 1}</td>
                      <td className="fw-bold">
                        {a.atividade}
                        {a.campo && <span className="text-muted small fw-normal ms-2">({a.campo})</span>}
                      </td>
                      <td>
                        <select
                          className={`form-select form-select-sm ${
                            a.status_inicial === 'Concluído' ? 'border-success' :
                            a.status_inicial === 'Não se aplica' ? 'border-secondary' : 'border-warning'
                          }`}
                          value={a.status_inicial}
                          disabled={salvandoId === a.id}
                          onChange={(e) => alterarStatus(a, e.target.value)}
                        >
                          {(statusDisponiveis.length ? statusDisponiveis
                            : ['Concluído', 'Não iniciada', 'Não se aplica']).map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                      <td className="text-center">
                        <button className="btn btn-sm btn-outline-danger" onClick={() => setExcluir(a)}
                          title="Remover atividade">
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Nova atividade */}
            <div className="border rounded p-3 bg-light mt-3">
              <div className="fw-bold mb-2" style={{ color: '#212529' }}>
                <i className="fas fa-plus me-2 text-primary"></i>
                Nova atividade de {tipo.toLowerCase()}
              </div>
              <div className="row g-2 align-items-end">
                <div className="col-md-6">
                  <label className="form-label small fw-bold mb-1">Nome da atividade</label>
                  <input type="text" className="form-control" placeholder="Ex: Kit de boas-vindas"
                    value={novaAtividade} onChange={(e) => setNovaAtividade(e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-bold mb-1">Status inicial</label>
                  <select className="form-select" value={novoStatus} onChange={(e) => setNovoStatus(e.target.value)}>
                    {(statusDisponiveis.length ? statusDisponiveis
                      : ['Concluído', 'Não iniciada', 'Não se aplica']).map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <button className="btn btn-success w-100" onClick={criarAtividade} disabled={criando}>
                    {criando
                      ? <span className="spinner-border spinner-border-sm"></span>
                      : <><i className="fas fa-plus me-2"></i>Adicionar</>}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal de exclusão */}
      {excluir && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                  <i className="fas fa-exclamation-triangle me-2 text-danger"></i>
                  Remover atividade
                </h5>
                <button type="button" className="btn-close" onClick={() => setExcluir(null)}></button>
              </div>
              <div className="modal-body">
                Remover <strong>{excluir.atividade}</strong> do checklist de
                admissão de <strong>{clienteAtual?.cliente}</strong>?
                <div className="small text-muted mt-2">
                  Não afeta os checklists de admissões já registradas.
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={() => setExcluir(null)}>
                  <i className="fas fa-times me-2"></i>Cancelar
                </button>
                <button className="btn btn-danger" onClick={confirmarExclusao}>
                  <i className="fas fa-trash me-2"></i>Remover
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BpoRhAtividadesAdmissao;
